import { Npc, select } from "gamedata/GameData";
import { ReactElement, useState } from "react";
import { Link, useLoaderData, useNavigate, useParams } from "react-router";
import { DevScreenshotUplodaer } from "~/components/form/DevScreenshotUploader";
import { ItemLink } from "~/components/item/ItemLink";
import { BuffList } from "~/components/npc/BuffList";
import { NpcLink, npcTitleColor } from "~/components/npc/NpcLink";
import { RaceLink } from "~/components/npc/RaceLink";
import { QuestLink } from "~/components/quest/QuestLink";
import { QuestProfList } from "~/components/quest/QuestProfList";
import { QuestRaceList } from "~/components/quest/QuestRaceList";
import { QuestRewardList } from "~/components/quest/QuestRewardList";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Facts } from "~/components/ui/Facts";
import { getIconFullUrl, Icon } from "~/components/ui/Icon";
import { InfoBox } from "~/components/ui/InfoBox";
import { Map as MapBox } from "~/components/ui/Map";
import { PageTitle } from "~/components/ui/PageTitle";
import { renderTableTr, Table } from "~/components/ui/Table";
import { Tabs } from "~/components/ui/Tabs";
import { stripSlashes } from "~/util/common";
import { GameVer, tt, useRealm, useT, useTurl } from "~/util/Lang";
import { callApi, type MetaFunc, type OurLoaderArgs } from "~/util/Loaders";
import { getLocList } from "~/util/loc";
import { getDescription, getLocs, npcMdPage } from "~/util/npc";
import { createSorting, SortBy, SortColumnName } from "~/util/Sorting";
import { homePage } from "~/util/titles";

import { z } from "zod";
import { InputMD } from "~/components/form/InputMd";
import { NpcStats } from "~/components/npc/NpcStats";
import { SkillList } from "~/components/npc/SkillList";
import { Summon } from "~/components/npc/Summon";
import { Comments, getComments } from "~/components/site/Comments";
import { MdContent } from "~/components/site/MdContent";
import { NotFoundInfo } from "~/components/site/NotFoundInfo";
import { Heading3 } from "~/components/ui/Heading";
import { mergeSkillType } from "~/util/skill";
import { logPageView } from "~/util/stats";
import { editPageApi } from "../api/mdPages.pages";
import css from "./classes.module.css";

type NpcDropSort = {
  countMin: number;
  countMax: number;
  chance: number;
  item: {
    id: number;
    icon: string;
    itemName: string;
    name: string;
  };
};

type NpcSellSort = {
  item: {
    id: number;
    itemName: string;
    icon: string;
    name: string;
    defaultPrice: number | null;
  };
};

const SORTER_DROP = createSorting<NpcDropSort>({
  name: (a, b) => (a.item.name ?? "").localeCompare(b.item.name ?? ""),
  chance: (a, b) => (a.chance ?? 0) - (b.chance ?? 0),
  count: (a, b) =>
    (a.countMin + a.countMax) / 2 - (b.countMin + b.countMax) / 2,
});

const SORTER_SHOP = createSorting<NpcSellSort>({
  name: (a, b) => (a.item.name ?? "").localeCompare(b.item.name ?? ""),
  price: (a, b) => (a.item.defaultPrice ?? 0) - (b.item.defaultPrice ?? 0),
});

type NpcAlt = {
  name: string;
  nick: string;
  nickColor: Npc["nickColor"];
  npcName: string;
  level: number | null;
  gver: GameVer;
};

async function loadData(
  la: OurLoaderArgs,
): Promise<
  | { status: "404"; alts: NpcAlt[] }
  | { status: "ok"; data: Awaited<ReturnType<typeof makeData>> }
> {
  const t = tt.bind(null, la.realm.lang);
  const params = Params.parse(la.params);
  const npc = la.gd.npcs.get(params.npcId);

  if (npc == null) {
    const alts: NpcAlt[] = [];
    for (const altGD of Object.values(la.gd.all_gd!)) {
      const altNpc = altGD.npcs.get(params.npcId);
      if (altNpc) {
        alts.push({
          name: t(altNpc.name),
          nick: t(altNpc.nick),
          nickColor: altNpc.nickColor,
          npcName: altNpc.npcName,
          level: altNpc.level,
          gver: altGD.gver,
        });
      }
    }

    la.res.status(404);
    return {
      status: "404",
      alts,
    };
  }

  return { status: "ok", data: await makeData(la, npc) };
}

const Params = z.object({ npcId: z.string() });
async function makeData(la: OurLoaderArgs, npc: Npc) {
  const t = tt.bind(null, la.realm.lang);
  const params = Params.parse(la.params);

  // Log
  logPageView(la);
  //

  const data = {
    npc: select(npc, {
      id: true,
      type: true,
      classes: { select: true },
      npcName: true,
      name: { en: true, ru: true },
      nick: { en: true, ru: true },
      nickColor: true,
      level: true,
      race: { raceName: true, icon: true, name: t },
      pAtk: true,
      pDef: true,
      mAtk: true,
      mDef: true,
      pSpd: true,
      mSpd: true,
      con: true,
      dex: true,
      int: true,
      men: true,
      str: true,
      wit: true,
      pCritical: true,
      accuracy: true,
      evasion: true,
      baseMovingSpeed: { select: true },
      orgHp: true,
      orgMp: true,
      exp: true,
      sp: true,
      agro: true,
      agroRange: true,
      locs: {
        select: { id: true, name: t, locName: true },
      },
      spawns: { select: { pos: { select: { x: true, y: true, z: true } } } },
      startsQuests: {
        select: {
          id: true,
          uname: true,
          name: t,
          minLevel: true,
          maxLevel: true,
          reward: { exp: true, sp: true, items: { select: { icon: true } } },
          requirements: {
            category: true,
            items: { select: { select: { name: t } } },
            occupation: {
              select: { id: true, profName: true, name: t },
            },
            races: {
              select: {
                raceName: true,
                icon: true,
                name: t,
              },
            },
          },
        },
      },
      inQuests: {
        select: {
          id: true,
          uname: true,
          name: t,
          minLevel: true,
          maxLevel: true,
          reward: { exp: true, sp: true, items: { select: { icon: true } } },
          requirements: {
            category: true,
            items: { select: { select: { name: t } } },
            occupation: {
              select: { id: true, profName: true, name: t },
            },
            races: {
              select: {
                raceName: true,
                icon: true,
                name: t,
              },
            },
          },
        },
      },
      multisell: {
        select: {
          requiredItems: {
            select: {
              count: true,
              item: {
                id: true,
                itemName: true,
                icon: true,
                name: t,
              },
            },
          },
          resultItems: {
            select: {
              count: true,
              item: {
                id: true,
                itemName: true,
                icon: true,
                name: t,
              },
            },
          },
        },
      },
      merchant: {
        tax: true,
        sellLists: {
          select: {
            items: {
              select: {
                item: {
                  id: true,
                  itemName: true,
                  icon: true,
                  name: t,
                  defaultPrice: true,
                },
              },
            },
          },
        },
      },
      dropList: {
        select: {
          countMin: true,
          countMax: true,
          chance: true,
          item: {
            id: true,
            icon: true,
            itemName: true,
            name: t,
          },
        },
      },
      spoilList: {
        select: {
          countMin: true,
          countMax: true,
          chance: true,
          item: {
            id: true,
            icon: true,
            itemName: true,
            name: t,
          },
        },
      },
      herbList: {
        select: {
          countMin: true,
          countMax: true,
          chance: true,
          item: {
            id: true,
            icon: true,
            itemName: true,
            name: t,
          },
        },
      },
      buffList: {
        select: {
          group: true,
          minLevel: true,
          maxLevel: true,
          skill: {
            id: true,
            skillName: true,
            cardName: true,
            level: true,
            icon: true,
            name: t,
          },
        },
      },
      skills: {
        select: {
          id: true,
          cardName: true,
          icon: true,
          skillName: true,
          name: t,
          level: true,
          operateType: true,
        },
      },
      screenshots: { select: { filename: true, height: true, width: true } },
      soulshotCount: true,
      spiritshotCount: true,
      model: {
        meshName: true,
      },
      summonLevels: {
        select: {
          npcName: true,
          name: t,
          race: { icon: true, name: t },
          level: true,
          orgHp: true,
          orgMp: true,
          accuracy: true,
          baseMovingSpeed: (x) => x[1],
          evasion: true,
          mAtk: true,
          mDef: true,
          mSpd: true,
          pAtk: true,
          pCritical: true,
          pDef: true,
          pSpd: true,
        },
      },
    }),
  };
  return {
    ...data,
    mdPage: await npcMdPage(la, params.npcId),
    comments: await getComments({
      id: data.npc.npcName,
      type: "npc",
      args: la,
    }),
    authUser: la.req.sess?.user,
  };
}

type LoaderData = Awaited<ReturnType<typeof loader>>;

export const loader = async (la: OurLoaderArgs) => {
  const data = await loadData(la);
  return { ...data, authUser: la.req.sess?.user };
};

export const meta: MetaFunc<LoaderData> = (cx) => {
  if (cx.data.status === "404") {
    return {
      title: "NPC not found - " + homePage(cx.realm),
    };
  }

  const npc = cx.data.data.npc;
  const picture = npc.screenshots[0];
  let nameRu = npc.name.ru;
  let nameEn = npc.name.en;
  const level = npc.level;
  if (npc.nick.en !== "") nameEn = `${npc.nick.en} ${npc.name.en}`;
  if (npc.nick.ru !== "") nameEn = `${npc.nick.en} ${npc.name.en}`;

  const name = cx.t({
    en: `${nameEn} Lvl. ${level}`,
    ru: `${nameRu} (${nameEn}) Ур. ${level}`,
  });

  return {
    title: `${name} - ${cx.t({ en: "NPCs", ru: "НПЦ" })} - ${homePage(
      cx.realm,
    )}`,

    ogTitle: cx.t({
      en: `NPC: ${nameEn} Lvl. ${level}`,
      ru: `НПЦ: ${nameRu} (${nameEn}) Ур. ${level}`,
    }),
    description: getDescription({
      npc,
      locs: getLocs(npc.locs, cx.realm),
      realm: cx.realm,
    }),
    ogImage: picture
      ? `https://l2hub.info/img/npc/${picture.filename}`
      : getIconFullUrl(npc.race.icon, cx.realm.game),
    canonicalUrl: `/npcs/${npc.npcName}`,
  };
};

export default function Npcs() {
  const params = useParams();
  const loaderData = useLoaderData<typeof loader>();
  const t = useT();
  const turl = useTurl();
  const realm = useRealm();
  const navigate = useNavigate();

  const [spoil, setSpoil] = useState<string[]>([]);
  const [herbs, setHerbs] = useState<string[]>([]);
  const [drop, setDrop] = useState<string[]>([]);
  const [shop, setShop] = useState<string[]>([]);

  if (loaderData.status === "404") {
    return (
      <NotFoundInfo
        title={t({ en: "NPC not found", ru: "НПЦ не найден" })}
        altsTitle={t({
          en: "This NPC exists in other gave versions:",
          ru: "Этот НПЦ доступен в других версиях игры:",
        })}
        alts={loaderData.alts.map((altNpc) => ({
          gver: altNpc.gver,
          node: <NpcLink npc={altNpc} />,
        }))}
      />
    );
  }

  const data = loaderData.data;
  const npc = data.npc;
  const merchant = npc.merchant;

  function sortBySpoil(): SortBy {
    return {
      value: spoil,
      update: (next) => setSpoil(next),
    };
  }

  function sortByHerbs(): SortBy {
    return {
      value: herbs,
      update: (next) => setHerbs(next),
    };
  }

  function sortByDrop(): SortBy {
    return {
      value: drop,
      update: (next) => setDrop(next),
    };
  }

  function sortByShop(): SortBy {
    return {
      value: shop,
      update: (next) => setShop(next),
    };
  }

  const nick = t({ en: data.npc.nick.en, ru: data.npc.nick.ru });
  const name = t({ en: data.npc.name.en, ru: data.npc.name.ru });

  let fullName = t({ en: npc.name.en, ru: npc.name.ru });
  if (nick !== "") {
    fullName = t({
      en: `${npc.nick.en} ${npc.name.en}`,
      ru: `${npc.nick.ru} ${npc.name.ru}`,
    });
  }
  const npcsTitle = t({ en: "NPCs", ru: "НПЦ" });

  const breadcrumbList = [
    { title: npcsTitle, url: turl("/npcs") },
    { title: fullName, url: turl(`/npcs/${data.npc.npcName}`) },
  ];
  const picture = npc.screenshots[0];

  const skillsRows: Array<(typeof data.npc.skills)[number] | ReactElement> = [];
  let lastOperateType = "";
  for (const skill of data.npc.skills) {
    if (mergeSkillType(skill.operateType) !== lastOperateType) {
      skillsRows.push(
        renderTableTr(
          { key: skill.id + "_" + skill.level },
          <td colSpan={3} className={css.levelTd}>
            {t({ en: "Type", ru: "Тип" })}{" "}
            {skill.operateType === "P"
              ? t({ en: "Passive", ru: "Пассивные" })
              : t({ en: "Active", ru: "Активные" })}
          </td>,
        ),
      );
      lastOperateType = mergeSkillType(skill.operateType);
    }
    skillsRows.push(skill);
  }

  async function editNPCPage() {
    const id = data.npc.npcName;
    const page = `/npcs/${id}`;
    if (!data.authUser) {
      const ref = turl(page);
      return navigate(`/login?r=${ref}`);
    }
    // setLoading(true);
    if (data.mdPage) {
      return navigate(`${page}/edit-page`);
    }

    const result = await callApi(editPageApi, {
      input: {
        game: [],
        target: { _: "npc", npcName: id },
      },
      realm,
    });

    switch (result.t) {
      case "success":
        // setLoading(false);
        return navigate(turl(`${page}/edit-page`));
      case "error":
        console.error(result);

        // setLoading(false);
        break;
    }
  }

  return (
    <div key={npc.id}>
      <div className="content">
        <Breadcrumb items={breadcrumbList} />
        <div className="columns">
          <div className="column is-three-quarters">
            <PageTitle>
              <span style={{ display: "flex", flexDirection: "column" }}>
                {data.npc.nick && (
                  <span
                    style={{
                      color: npcTitleColor({
                        nickColor: npc.nickColor,
                        type: npc.type,
                      }),
                      fontSize: 18,
                      lineHeight: 1,
                    }}
                  >
                    {nick}
                  </span>
                )}
                <span style={{ lineHeight: 1 }}>{name}</span>
              </span>
            </PageTitle>
            <SkillList skills={npc.skills} />
            {/* {DEV && npc.classes.map((x, i) => <div key={i}>{x}</div>)}
            {DEV &&
              npc.spawns.map((x, i) => (
                <div key={i}>{JSON.stringify(x.pos)}</div>
              ))} */}
            <div>
              {npc.spawns.length !== 0 ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div className="npc-map">
                    <MapBox spawns={npc.spawns} />
                  </div>
                </div>
              ) : null}
            </div>
            {npc.buffList.length !== 0 && <BuffList buffList={npc.buffList} />}
            {npc.summonLevels.length !== 0 && (
              <Summon summonLevels={npc.summonLevels} activeNpc={npc} />
            )}
            {data.mdPage.content && (
              <>
                <Heading3>
                  {t({
                    en: "Wiki",
                    ru: "Wiki",
                  })}
                </Heading3>
                <MdContent>{data.mdPage.content}</MdContent>
              </>
            )}
          </div>
          <div className="column">
            <InfoBox
              title={t({
                en: "NPC Facts",
                ru: "Краткая информация",
              })}
            >
              <Facts
                facts={[
                  {
                    name: t({ en: "Aggr", ru: "Агро" }),
                    value: (() => {
                      return (
                        <span style={{ color: npc.agro ? "red" : "green" }}>
                          {npc.agro
                            ? t({ en: "Aggressive", ru: "Агрессивный" })
                            : t({ en: "Passive", ru: "Пассивный" })}
                        </span>
                      );
                    })(),
                    show: npc.agro != null,
                  },
                  { name: "NPC ID", value: npc.id },
                  {
                    name: "Англ. название",
                    value: npc.name.en,
                    show: realm.lang === "ru",
                  },
                  {
                    name: t({
                      en: "Level",
                      ru: "Уровень",
                    }),
                    value: npc.level,
                  },
                  {
                    name: t({
                      en: "Race",
                      ru: "Раса",
                    }),
                    value: npc.race && <RaceLink race={npc.race} />,
                  },
                  {
                    name: t({
                      en: "Reward",
                      ru: "Награда",
                    }),
                    value: [[npc.exp, "EXP"] as const, [npc.sp, "SP"] as const]
                      .filter(([v]) => !!v)
                      .map(([v, u]) => `${Math.round(v ?? 0)} ${u}`)
                      .join(", "),
                  },
                  {
                    name: t({
                      en: "HP / MP",
                      ru: "ХП / МП",
                    }),
                    value: (
                      <span>
                        <span style={{ color: "red" }}>
                          {Math.round(npc.orgHp ?? 0)}
                        </span>{" "}
                        /{" "}
                        <span style={{ color: "#008cff" }}>
                          {Math.round(npc.orgMp ?? 0)}
                        </span>
                      </span>
                    ),
                  },
                  {
                    name: t({
                      en: "Location",
                      ru: "Локация",
                    }),
                    value: getLocList(npc.locs),
                    show: npc.locs.length > 0,
                  },
                  {
                    name: t({
                      en: "Agro Range",
                      ru: "Agro Range",
                    }),
                    value: npc.agroRange ?? "-",
                  },
                ]}
              />
              <InputMD value={`:npc[${data.npc.npcName}]`} />
              {data.npc.model && (
                <div style={{ marginTop: 20 }}>
                  <Link to={"./modelviewer"} className={css.buttonModelviewer}>
                    {t({ en: "View in 3D", ru: "3D-Модель" })}
                  </Link>
                </div>
              )}
              {picture && (
                <>
                  <div
                    style={{
                      color: "#fff",
                      textAlign: "center",
                      padding: "20px 0 10px 0",
                      fontSize: 16,
                      fontWeight: "400",
                    }}
                  >
                    {t({ en: "Screenshot", ru: "Скриншот" })}
                  </div>
                  <div style={{ marginBottom: 5, textAlign: "center" }}>
                    <img
                      style={{
                        maxWidth: picture.width,
                        width: "100%",
                        borderRadius: 3,
                        boxShadow: "0 3px 10px rgb(0 0 0 / 35%)",
                      }}
                      src={`/img/npc/${picture.filename}`}
                      alt={`NPC ${data.npc.name} Lineage 2`}
                    />
                  </div>
                </>
              )}
              <DevScreenshotUplodaer
                kind="npc"
                ident={npc.npcName}
                name={name}
              />
            </InfoBox>

            <NpcStats npc={npc} />
          </div>
        </div>
      </div>
      <div style={{ marginTop: 20, overflowX: "auto" }}>
        <Tabs
          current={params["*"] ?? ""}
          link={(id) => turl(stripSlashes(`/npcs/${npc.npcName}/${id}`))}
          tabs={[
            {
              id: "comments",
              title: t({ en: "Comments", ru: "Комментарии" }),
              body: () => (
                <div>
                  <Comments
                    comments={data.comments}
                    authUser={data.authUser}
                    subject={{ objType: "npc", objUname: data.npc.npcName }}
                  />
                </div>
              ),
            },
            {
              id: "drop",
              title: t({ en: "Drop", ru: "Дроп" }),
              count: data.npc.dropList.length,
              body: () => (
                <Table
                  data={SORTER_DROP.apply(data.npc.dropList, drop.join(","))}
                  id={(_drop, index) => index}
                  cols={[
                    {
                      title: "",
                      cell: (drop) => (
                        <Icon icon={drop.item.icon} alt={drop.item.name} />
                      ),
                      thPadding: "0",
                      padding: "8px 0 8px 8px",
                      width: 32 + 12,
                    },
                    {
                      title: (
                        <SortColumnName name="name" by={sortByDrop()}>
                          {t({ en: "Name", ru: "Имя" })}
                        </SortColumnName>
                      ),
                      cell: (drop) => (
                        <Link to={turl(`/items/${drop.item.itemName}`)}>
                          {drop.item.name ?? drop.item.itemName}
                        </Link>
                      ),
                    },
                    {
                      title: (
                        <SortColumnName name="chance" by={sortByDrop()}>
                          {t({ en: "Chance", ru: "Шанс" })}
                        </SortColumnName>
                      ),
                      cell: (drop) => (
                        <>{parseFloat(drop.chance.toPrecision(4))}%</>
                      ),
                    },
                    {
                      title: (
                        <SortColumnName name="count" by={sortByDrop()}>
                          {t({ en: "Count", ru: "Кол-во" })}
                        </SortColumnName>
                      ),
                      cell: (drop) => {
                        let ret = drop.countMin.toString();
                        if (drop.countMin !== drop.countMax) {
                          ret += " - " + drop.countMax.toString();
                        }
                        return ret;
                      },
                    },
                  ]}
                />
              ),
            },
            {
              id: "spoil",
              title: t({ en: "Spoil", ru: "Спойл" }),
              count: data.npc.spoilList.length,
              body: () => (
                <Table
                  data={SORTER_DROP.apply(data.npc.spoilList, spoil.join(","))}
                  id={(_spoil, index) => index}
                  cols={[
                    {
                      title: "",
                      cell: (spoil) => (
                        <Icon icon={spoil.item.icon} alt={spoil.item.name} />
                      ),
                      thPadding: "0",
                      padding: "8px 0 8px 8px",
                      width: 32 + 12,
                    },
                    {
                      title: (
                        <SortColumnName name="name" by={sortBySpoil()}>
                          {t({ en: "Name", ru: "Имя" })}
                        </SortColumnName>
                      ),
                      cell: (spoil) => (
                        <Link to={turl(`/items/${spoil.item.itemName}`)}>
                          {spoil.item.name ?? spoil.item.itemName}
                        </Link>
                      ),
                    },
                    {
                      title: (
                        <SortColumnName name="chance" by={sortBySpoil()}>
                          {t({ en: "Chance", ru: "Шанс" })}
                        </SortColumnName>
                      ),
                      cell: (spoil) => (
                        <>{parseFloat(spoil.chance.toPrecision(4))}%</>
                      ),
                    },
                    {
                      title: (
                        <SortColumnName name="count" by={sortBySpoil()}>
                          {t({ en: "Count", ru: "Кол-во" })}
                        </SortColumnName>
                      ),
                      cell: (spoil) => {
                        let ret = spoil.countMin.toString();
                        if (spoil.countMin !== spoil.countMax) {
                          ret += " - " + spoil.countMax.toString();
                        }
                        return ret;
                      },
                    },
                  ]}
                />
              ),
            },
            {
              id: "herbs",
              title: t({ en: "Herbs", ru: "Гербы" }),
              count: data.npc.herbList.length,
              body: () => (
                <Table
                  data={SORTER_DROP.apply(data.npc.herbList, herbs.join(","))}
                  id={(_herb, index) => index}
                  cols={[
                    {
                      title: "",
                      cell: (herb) => (
                        <Icon icon={herb.item.icon} alt={herb.item.name} />
                      ),
                      thPadding: "0",
                      padding: "8px 0 8px 8px",
                      width: 32 + 12,
                    },
                    {
                      title: (
                        <SortColumnName name="name" by={sortByHerbs()}>
                          {t({ en: "Name", ru: "Имя" })}
                        </SortColumnName>
                      ),
                      cell: (herb) => (
                        <Link to={turl(`/items/${herb.item.itemName}`)}>
                          {herb.item.name ?? herb.item.itemName}
                        </Link>
                      ),
                    },
                    {
                      title: (
                        <SortColumnName name="chance" by={sortByHerbs()}>
                          {t({ en: "Chance", ru: "Шанс" })}
                        </SortColumnName>
                      ),
                      cell: (herb) => (
                        <>{parseFloat(herb.chance.toPrecision(4))}%</>
                      ),
                    },
                    {
                      title: (
                        <SortColumnName name="count" by={sortByHerbs()}>
                          {t({ en: "Count", ru: "Кол-во" })}
                        </SortColumnName>
                      ),
                      cell: (herb) => {
                        let ret = herb.countMin.toString();
                        if (herb.countMin !== herb.countMax) {
                          ret += " - " + herb.countMax.toString();
                        }
                        return ret;
                      },
                    },
                  ]}
                />
              ),
            },
            ...(merchant
              ? merchant.sellLists.map((list, i) => {
                  return {
                    id: "sell-" + (i + 1),
                    title: t({ en: "Sells", ru: "Продаёт" }),
                    count: list.items.length,
                    body: () => (
                      <Table
                        data={SORTER_SHOP.apply(list.items, shop.join(","))}
                        id={(_drop, index) => index}
                        cols={[
                          {
                            title: "",
                            cell: (drop) => (
                              <Icon
                                icon={drop.item.icon}
                                alt={drop.item.name}
                              />
                            ),
                            thPadding: "0",
                            padding: "8px 0 8px 8px",
                            width: 32 + 12,
                          },
                          {
                            title: (
                              <SortColumnName name="name" by={sortByShop()}>
                                {t({ en: "Name", ru: "Имя" })}
                              </SortColumnName>
                            ),
                            cell: (drop) => (
                              <Link to={turl(`/items/${drop.item.itemName}`)}>
                                {drop.item.name}
                              </Link>
                            ),
                          },
                          {
                            title: (
                              <SortColumnName name="price" by={sortByShop()}>
                                {t({ en: "Price", ru: "Цена" })}
                              </SortColumnName>
                            ),
                            cell: (drop) => (
                              <>
                                {drop.item.defaultPrice !== null
                                  ? Math.round(
                                      drop.item.defaultPrice *
                                        (1 + merchant.tax / 100),
                                    )
                                  : null}
                              </>
                            ),
                          },
                          {
                            title: t({ en: "Tax", ru: "Комиссия" }),
                            cell: (_drop) => <>{merchant.tax}</>,
                          },
                        ]}
                      />
                    ),
                  };
                })
              : []),
            {
              id: "starts",
              title: t({ en: "Starts", ru: "Начинает" }),
              count: data.npc.startsQuests.length,
              body: () => (
                <Table
                  data={data.npc.startsQuests}
                  id={(quest) => quest.id}
                  cols={[
                    {
                      title: t({ en: "Name", ru: "Имя" }),
                      cell: (quest) => <QuestLink quest={quest} />,
                    },
                    {
                      title: t({ en: "Level", ru: "Уровень" }),
                      cell: (quest) => (
                        <>
                          {quest.minLevel ?? ""}
                          {quest.maxLevel ? "-" + quest.maxLevel : null}
                        </>
                      ),
                    },
                    {
                      title: t({ en: "Reward", ru: "Награда" }),
                      cell: (quest) => (
                        <QuestRewardList reward={quest.reward} />
                      ),
                    },
                    {
                      title: t({ en: "Race", ru: "Раса" }),
                      cell: (quest) => (
                        <QuestRaceList races={quest.requirements.races} />
                      ),
                    },
                    {
                      title: t({ en: "Class", ru: "Класс" }),
                      cell: (quest) => (
                        <QuestProfList profs={quest.requirements.occupation} />
                      ),
                    },
                  ]}
                />
              ),
            },
            {
              id: "in-quests",
              title: t({ en: "In Quests", ru: "В квестах" }),
              count: data.npc.inQuests.length,
              body: () => (
                <Table
                  data={data.npc.inQuests}
                  id={(quest) => quest.id}
                  cols={[
                    {
                      title: t({ en: "Name", ru: "Имя" }),
                      cell: (quest) => <QuestLink quest={quest} />,
                    },
                    {
                      title: t({ en: "Level", ru: "Уровень" }),
                      cell: (quest) => (
                        <>
                          {quest.minLevel ?? ""}
                          {quest.maxLevel ? "-" + quest.maxLevel : null}
                        </>
                      ),
                    },
                    {
                      title: t({ en: "Reward", ru: "Награда" }),
                      cell: (quest) => (
                        <QuestRewardList reward={quest.reward} />
                      ),
                    },
                    {
                      title: t({ en: "Race", ru: "Раса" }),
                      cell: (quest) => (
                        <QuestRaceList races={quest.requirements.races} />
                      ),
                    },
                    {
                      title: t({ en: "Class", ru: "Класс" }),
                      cell: (quest) => (
                        <QuestProfList profs={quest.requirements.occupation} />
                      ),
                    },
                  ]}
                />
              ),
            },
            {
              id: "levels",
              title: t({ en: "Levels", ru: "Уровни" }),
              count: data.npc.summonLevels.length,
              body: () => (
                <Table
                  data={data.npc.summonLevels}
                  id={(summon, index) => index}
                  cols={[
                    {
                      title: "Level",
                      cell: (summon) => (
                        <div>
                          <NpcLink
                            npc={{
                              npcName: summon.npcName,
                              nick: null,
                              nickColor: "default",
                              name: summon.name,
                              level: summon.level,
                              race: summon.race,
                            }}
                            showIcon={true}
                          />
                        </div>
                      ),
                    },
                    {
                      title: "HP/MP",
                      cell: (summon) => (
                        <span>
                          <span style={{ color: "red" }}>
                            {Math.round(summon.orgHp ?? 0)}
                          </span>{" "}
                          /{" "}
                          <span style={{ color: "#008cff" }}>
                            {Math.round(summon.orgMp ?? 0)}
                          </span>
                        </span>
                      ),
                    },
                    {
                      title: "P.Atk.",
                      cell: (summon) => summon.pAtk,
                    },
                    {
                      title: "P.Def",
                      cell: (summon) => summon.pDef,
                    },
                    {
                      title: "A.Spd.",
                      cell: (summon) => summon.pSpd,
                    },
                    {
                      title: "M.Atk.",
                      cell: (summon) => summon.mAtk,
                    },
                    {
                      title: "M.Def.",
                      cell: (summon) => summon.mDef,
                    },
                    {
                      title: "C.Spd.",
                      cell: (summon) => summon.mSpd,
                    },
                    {
                      title: "Acc",
                      cell: (summon) => summon.accuracy,
                    },
                    {
                      title: "Evas",
                      cell: (summon) => summon.evasion,
                    },
                    {
                      title: "Crit",
                      cell: (summon) => summon.pCritical,
                    },
                    {
                      title: "Spd",
                      cell: (summon) => summon.baseMovingSpeed,
                    },
                  ]}
                />
              ),
            },
            // {
            //   id: "skills",
            //   title: t({ en: "Skills", ru: "Умения" }),
            //   count: data.npc.skills.length,
            //   body: () => (
            //     <Table
            //       data={skillsRows}
            //       id={(skill) => skill.id}
            //       cols={[
            //         {
            //           title: "",
            //           cell: (skill) => (
            //             <Icon icon={skill.icon} alt={skill.name} />
            //           ),
            //           thPadding: "0",
            //           padding: "8px 0 8px 8px",
            //           width: 32 + 12,
            //         },
            //         {
            //           title: t({ en: "Name", ru: "Имя" }),
            //           cell: (skill) => (
            //             <div>
            //               <span style={{ color: "#fff", marginRight: "10px" }}>
            //                 {skill.name}
            //               </span>
            //               {realm.game === "c1" && (
            //                 <span style={{ color: "grey" }}>
            //                   [{skill.skillName}]
            //                 </span>
            //               )}
            //             </div>
            //           ),
            //         },
            //         {
            //           title: t({ en: "Level", ru: "Уровень" }),
            //           cell: (skill) => skill.level,
            //         },
            //       ]}
            //     />
            //   ),
            // },
            {
              id: "exchange",
              title: t({ en: "Exchange", ru: "Обменивает" }),
              count: data.npc.multisell.length,
              body: () => (
                <Table
                  data={data.npc.multisell}
                  id={(_ms, i) => i}
                  cols={[
                    {
                      title: "",
                      cell: (ms) => (
                        <Icon
                          icon={ms.resultItems[0].item.icon}
                          alt={ms.resultItems[0].item.name}
                        />
                      ),
                      thPadding: "0",
                      padding: "8px 0 8px 8px",
                      width: 32 + 12,
                    },
                    {
                      title: t({ en: "Result Item", ru: "Получаемый предмет" }),
                      cell: (ms) => (
                        <div>
                          <Link
                            to={turl(
                              `/items/${ms.resultItems[0].item.itemName}`,
                            )}
                          >
                            {ms.resultItems[0].item.name ??
                              ms.resultItems[0].item.itemName}
                          </Link>
                          {ms.resultItems[0].count &&
                            ` x${ms.resultItems[0].count}`}
                        </div>
                      ),
                    },
                    {
                      title: t({
                        en: "Required Items",
                        ru: "Необходимые предметы",
                      }),
                      cell: (ms) => (
                        <div>
                          {ms.requiredItems.map((req, i) => (
                            <div key={i}>
                              <ItemLink item={req.item} />
                              {req.count && ` x${req.count} `}
                            </div>
                          ))}
                        </div>
                      ),
                    },
                  ]}
                />
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
